import React, { useCallback, useEffect, useState } from 'react';
import { DashboardApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { notifyError, notifySuccess } from '../utils/statusShortcuts';
import './AddonsPage.css';

const NOK = new Intl.NumberFormat('nb-NO', {
  style: 'currency',
  currency: 'NOK',
  maximumFractionDigits: 0,
});

const formatCurrency = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? NOK.format(num) : '—';
};

const buildAddonCatalog = (summary) => {
  const available = Array.isArray(summary?.availableAddons) ? summary.availableAddons : [];
  const active = Array.isArray(summary?.activeAddons) ? summary.activeAddons : [];
  const activeMap = new Map(
    active
      .filter((addon) => addon?.addonCode)
      .map((addon) => [String(addon.addonCode), addon]),
  );

  const catalog = available.map((addon) => {
    const code = String(addon?.code || '');
    const activeEntry = activeMap.get(code) || null;
    return {
      ...addon,
      code,
      active: Boolean(activeEntry),
      pendingCancellation: activeEntry?.pendingCancellation === true,
      cancelEffectiveAt: activeEntry?.cancelEffectiveAt || null,
    };
  });

  active.forEach((addon) => {
    const code = String(addon?.addonCode || '');
    if (!code || catalog.some((entry) => entry.code === code)) return;
    catalog.push({
      id: addon.id || code,
      code,
      name: addon.name || code,
      description: addon?.metadata?.description || '',
      unitPrice: addon.unitPrice ?? null,
      unitName: addon.unitName ?? null,
      active: true,
      pendingCancellation: addon.pendingCancellation === true,
      cancelEffectiveAt: addon.cancelEffectiveAt || null,
      metadata: addon.metadata || {},
    });
  });

  return catalog;
};

const AddonsPage = () => {
  const { selectedCompany, user } = useAuth();
  const companyId = selectedCompany?.id || null;
  const isOwner = Boolean(selectedCompany?.isOwner) || Boolean(selectedCompany?.isSuperAdminAccess) || Boolean(user?.isAdmin);

  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [addonLoading, setAddonLoading] = useState({});

  const loadAddons = useCallback(async () => {
    if (!companyId) {
      setAddons([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const summary = await DashboardApi.getBillingSummary(companyId, {
        skipStripe: true,
        force: true,
        retries: 0,
      });
      setAddons(buildAddonCatalog(summary));
    } catch (err) {
      const message = err.message || 'Kunne ikke hente tillegg.';
      setError(message);
      notifyError(message, { title: 'Kunne ikke hente tillegg', source: 'Tillegg' });
      setAddons([]);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    loadAddons();
  }, [loadAddons]);

  useEffect(() => {
    if (success) {
      notifySuccess(success, { source: 'Tilleggsverktøy' });
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [success]);

  const handleAddonToggle = async (addon, activate) => {
    if (!addon || !companyId || !isOwner) return;

    const confirmMessage = activate
      ? `Vil du aktivere ${addon.name}? Dette kan påvirke faktureringen.`
      : `Vil du deaktivere ${addon.name}? Tillegget avsluttes ved periodeslutt.`;

    if (!window.confirm(confirmMessage)) return;

    setAddonLoading((prev) => ({ ...prev, [addon.code]: true }));
    setError(null);

    try {
      if (activate) {
        await DashboardApi.enableBillingAddon(companyId, addon.code);
        setSuccess(`${addon.name} ble aktivert.`);
      } else {
        await DashboardApi.disableBillingAddon(companyId, addon.code);
        setSuccess(`${addon.name} vil avsluttes ved periodeslutt.`);
      }
      await loadAddons();
    } catch (err) {
      const message = err.message || 'Kunne ikke oppdatere tillegget.';
      setError(message);
      notifyError(message, { title: 'Kunne ikke oppdatere tillegg', source: 'Tillegg' });
    } finally {
      setAddonLoading((prev) => ({ ...prev, [addon.code]: false }));
    }
  };

  const activeAddons = addons.filter((addon) => addon.active);
  const availableAddons = addons.filter((addon) => !addon.active);

  if (!isOwner) {
    return (
      <div className="page addons-page">
        <div className="addons-restricted">
          <div className="addons-restricted-icon">🔒</div>
          <h2>Ingen tilgang</h2>
          <p>Kun eiere kan administrere tilleggsverktøy.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page addons-page">
      <header className="addons-header">
        <div className="addons-header-content">
          <p className="addons-eyebrow">Selskap</p>
          <h1>Tilleggsverktøy</h1>
          <p className="addons-subtitle">
            Utvid chatbotens evner med ekstra funksjoner og tilgjengelige tillegg.
          </p>
        </div>
        <div className="addons-header-stats">
          <div className="addons-stat">
            <span className="addons-stat-value">{activeAddons.length}</span>
            <span className="addons-stat-label">Aktive</span>
          </div>
          <div className="addons-stat">
            <span className="addons-stat-value">{availableAddons.length}</span>
            <span className="addons-stat-label">Tilgjengelige</span>
          </div>
        </div>
      </header>

      {loading ? (
        <div className="addons-loading">
          <div className="addons-spinner" />
          <p>Laster tillegg...</p>
        </div>
      ) : addons.length === 0 ? (
        <div className="addons-empty">
          <div className="addons-empty-icon">🧩</div>
          <h2>Ingen tillegg tilgjengelig</h2>
          <p>
            Det finnes ingen tillegg for dette selskapet ennå.
            Kontakt oss hvis dere trenger flere funksjoner.
          </p>
        </div>
      ) : (
        <>
          {activeAddons.length > 0 && (
            <section className="addons-section">
              <h2 className="addons-section-title">
                <span className="addons-section-icon">✓</span>
                Aktive tillegg
              </h2>
              <div className="addons-grid">
                {activeAddons.map((addon) => {
                  const price = Number(addon?.unitPrice ?? 0);
                  const isLoading = addonLoading[addon.code];

                  return (
                    <article key={addon.id || addon.code} className="addon-card active">
                      <div className="addon-card-header">
                        <div className="addon-icon">🔧</div>
                        <span className="addon-status active">Aktiv</span>
                      </div>
                      <h3 className="addon-name">{addon.name || addon.code}</h3>
                      <p className="addon-description">
                        {addon.metadata?.description || addon.description || 'Ingen beskrivelse tilgjengelig.'}
                      </p>
                      <div className="addon-footer">
                        <div className="addon-price">
                          {price > 0 ? (
                            <>
                              <span className="addon-price-amount">{formatCurrency(price)}</span>
                              <span className="addon-price-period">/ mnd</span>
                            </>
                          ) : (
                            <span className="addon-price-included">Inkludert i plan</span>
                          )}
                        </div>
                        <button
                          type="button"
                          className="addon-btn deactivate"
                          onClick={() => handleAddonToggle(addon, false)}
                          disabled={isLoading}
                        >
                          {isLoading ? 'Behandler...' : 'Deaktiver'}
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          )}

          {availableAddons.length > 0 && (
            <section className="addons-section">
              <h2 className="addons-section-title">
                <span className="addons-section-icon">+</span>
                Tilgjengelige tillegg
              </h2>
              <div className="addons-grid">
                {availableAddons.map((addon) => {
                  const price = Number(addon?.unitPrice ?? 0);
                  const isLoading = addonLoading[addon.code];

                  return (
                    <article key={addon.id || addon.code} className="addon-card available">
                      <div className="addon-card-header">
                        <div className="addon-icon">🔧</div>
                        {price > 0 && (
                          <span className="addon-badge">Premium</span>
                        )}
                      </div>
                      <h3 className="addon-name">{addon.name || addon.code}</h3>
                      <p className="addon-description">
                        {addon.metadata?.description || addon.description || 'Ingen beskrivelse tilgjengelig.'}
                      </p>
                      <div className="addon-footer">
                        <div className="addon-price">
                          {price > 0 ? (
                            <>
                              <span className="addon-price-amount">{formatCurrency(price)}</span>
                              <span className="addon-price-period">/ mnd</span>
                            </>
                          ) : (
                            <span className="addon-price-included">Inkludert i plan</span>
                          )}
                        </div>
                        <button
                          type="button"
                          className="addon-btn activate"
                          onClick={() => handleAddonToggle(addon, true)}
                          disabled={isLoading}
                        >
                          {isLoading ? 'Behandler...' : 'Aktiver'}
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          )}
        </>
      )}

      <footer className="addons-footer">
        <p>
          Trenger du et tilpasset verktøy?{' '}
          <a href="mailto:hei@kichat.no">Kontakt oss</a> for å diskutere mulighetene.
        </p>
      </footer>
    </div>
  );
};

export default AddonsPage;
